/**
 * Atlas Clients — Slice 3 (Msg 550).
 *
 * Three routes / components:
 *   • AtlasClientsListPage        — /atlas/clients
 *   • AtlasClientDetailPage       — /atlas/clients/:client_id
 *   • ClientPickerModal           — used by AtlasSearchPage save flow
 *
 * The `saved_by_url` map from /atlas/saves/lookup is used by the
 * search page to fill/empty the bookmark icon per card.
 *
 * NO MOCKED items in this slice — the "Save to client portfolio"
 * toast stub is fully replaced by real persistence.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search as SearchIcon,
  Plus,
  X,
  Bookmark,
  ArrowLeft,
  Trash2,
  Loader2,
  Edit3,
  ExternalLink,
  UserPlus,
  Wallet,
} from "lucide-react";
import { api } from "@/lib/api";


// ─────────────────────────────────────────────────────────────────
// List page — /atlas/clients
// ─────────────────────────────────────────────────────────────────
export function AtlasClientsListPage() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/atlas/clients?q=${encodeURIComponent(q)}`);
      setClients(data.clients || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[atlas.clients] list fail", e);
      toast.error("Failed to load clients.");
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-white">
      <div className="p-6 md:p-10 max-w-5xl mx-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-tb-navy text-3xl md:text-4xl font-semibold"
                style={{ fontFamily: '"Playfair Display", serif' }}
                data-testid="atlas-clients-title">
              Clients
            </h1>
            <p className="mt-1 font-hand text-tb-navy/60 text-sm">
              Your client roster. Save promising search results to a client's portfolio, add notes, and turn them into eQuotes.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setLinkOpen(true)}
              data-testid="atlas-clients-link-btn"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border border-tb-navy/25 text-tb-navy font-hand text-sm">
              <UserPlus className="w-4 h-4" /> Link user
            </button>
            <button type="button" onClick={() => setCreateOpen(true)}
              data-testid="atlas-clients-new-btn"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">
              <Plus className="w-4 h-4" /> New client
            </button>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2 h-12 px-4 rounded-full border border-tb-navy/25 bg-white"
             data-testid="atlas-clients-search-bar">
          <SearchIcon className="w-4 h-4 text-tb-navy/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name…"
            data-testid="atlas-clients-search"
            className="flex-1 h-full outline-none font-hand text-tb-navy placeholder-tb-navy/35" />
        </div>

        <div className="mt-6 grid gap-3">
          {loading ? (
            <div className="font-hand text-tb-navy/50 text-sm inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : clients.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-6"
                 data-testid="atlas-clients-empty">
              <p className="font-hand text-tb-navy text-sm">No clients yet.</p>
              <p className="mt-1 font-hand text-tb-navy/60 text-xs">
                Click "New client" to build your roster — even a name is enough to get started.
              </p>
            </div>
          ) : (
            clients.map((c) => (
              <Link key={c.id} to={`/atlas/clients/${c.id}`}
                data-testid={`atlas-clients-row-${c.id}`}
                className="block rounded-xl border border-tb-navy/12 bg-white p-4 hover:border-tb-navy/25 hover:shadow-sm transition">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-tb-navy font-semibold text-lg truncate"
                         style={{ fontFamily: '"Playfair Display", serif' }}>
                      {c.display_name}
                    </div>
                    <div className="mt-0.5 font-hand text-[11px] text-tb-navy/60">
                      {c.save_count || 0} saved · updated {new Date(c.updated_at).toLocaleDateString()}
                      {c.email && <> · {c.email}</>}
                    </div>
                  </div>
                  <ArrowLeft className="w-4 h-4 rotate-180 text-tb-navy/40" />
                </div>
              </Link>
            ))
          )}
        </div>
      </div>

      {createOpen && (
        <ClientCreateModal
          onClose={() => setCreateOpen(false)}
          onCreated={(c) => { setCreateOpen(false); nav(`/atlas/clients/${c.id}`); }}
        />
      )}
      {linkOpen && (
        <LinkExistingUserModal
          onClose={() => setLinkOpen(false)}
          onLinked={(c) => { setLinkOpen(false); nav(`/atlas/clients/${c.id}`); }}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Detail page — /atlas/clients/:client_id
// ─────────────────────────────────────────────────────────────────
export function AtlasClientDetailPage() {
  const { client_id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [saves, setSaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get(`/atlas/clients/${client_id}`),
        api.get(`/atlas/clients/${client_id}/saves`),
      ]);
      setClient(c.data.client);
      setNotesDraft(c.data.client?.notes || "");
      setSaves(s.data.saves || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[atlas.client] fetch fail", e);
      toast.error("Client not found.");
      nav("/atlas/clients");
    } finally { setLoading(false); }
  }, [client_id, nav]);

  useEffect(() => { refresh(); }, [refresh]);

  const saveNotes = async () => {
    try {
      const { data } = await api.patch(`/atlas/clients/${client_id}`, { notes: notesDraft });
      setClient(data.client);
      setEditingNotes(false);
      toast.success("Notes updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save notes.");
    }
  };

  const deleteSave = async (saveId) => {
    if (!window.confirm("Remove this save from the portfolio?")) return;
    try {
      await api.delete(`/atlas/saves/${saveId}`);
      setSaves((prev) => prev.filter((s) => s.id !== saveId));
      toast.success("Removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove.");
    }
  };

  const archiveClient = async () => {
    if (!window.confirm(`Archive ${client?.display_name}? Their saves will be hidden too.`)) return;
    try {
      await api.delete(`/atlas/clients/${client_id}`);
      toast.success("Client archived.");
      nav("/atlas/clients");
    } catch (e) {
      toast.error("Failed to archive.");
    }
  };

  if (loading) {
    return <div className="p-10 font-hand text-tb-navy/50 inline-flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>;
  }
  if (!client) return null;
  const tp = client.travel_prefs || {};

  return (
    <div className="min-h-screen bg-white">
      <div className="p-6 md:p-10 max-w-5xl mx-auto">
        <Link to="/atlas/clients"
              data-testid="atlas-client-back"
              className="inline-flex items-center gap-1 font-hand text-xs text-tb-navy/60 hover:text-tb-navy">
          <ArrowLeft className="w-3.5 h-3.5" /> All clients
        </Link>

        {/* Profile header */}
        <div className="mt-4 rounded-2xl border border-tb-navy/12 bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-tb-navy text-3xl font-semibold truncate"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                  data-testid="atlas-client-name">
                {client.display_name}
              </h1>
              <div className="mt-2 flex flex-wrap gap-3 font-hand text-[12px] text-tb-navy/70">
                {client.email && <span data-testid="atlas-client-email">📧 {client.email}</span>}
                {client.phone && <span data-testid="atlas-client-phone">📞 {client.phone}</span>}
                {tp.family_size && <span>👨‍👩‍👧 family of {tp.family_size}</span>}
                {tp.traveler_type && !tp.family_size && <span>👥 {tp.traveler_type}</span>}
                {tp.budget_band && <span>💰 {tp.budget_band}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {client.linked_user_id ? (
                <Link to={`/atlas/clients/${client_id}/book`}
                  data-testid="atlas-client-book-btn"
                  className="h-8 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs inline-flex items-center gap-1"
                  title="Book a hotel for this client">
                  <Wallet className="w-3.5 h-3.5" /> Book for client
                </Link>
              ) : (
                <span className="hidden md:inline-block h-8 px-3 rounded-full border border-tb-navy/15 text-tb-navy/50 font-hand text-[11px] items-center"
                      title="Link a TravelBio user first to enable booking"
                      data-testid="atlas-client-book-locked">
                  🔒 Link user to book
                </span>
              )}
              <button type="button" onClick={() => setEditingProfile(true)}
                data-testid="atlas-client-edit"
                className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-tb-navy/15 text-tb-navy/60 hover:bg-tb-navy/5"
                title="Edit profile">
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={archiveClient}
                data-testid="atlas-client-archive"
                className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-red-200 text-red-700 hover:bg-red-50"
                title="Archive client">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Notes */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50">Advisor notes</p>
              {!editingNotes && (
                <button type="button" onClick={() => setEditingNotes(true)}
                  data-testid="atlas-client-notes-edit"
                  className="text-[10px] font-hand text-tb-navy/60 hover:text-tb-navy">Edit</button>
              )}
            </div>
            {editingNotes ? (
              <div>
                <textarea value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
                  data-testid="atlas-client-notes-textarea"
                  rows={3}
                  className="w-full rounded-lg border border-tb-navy/20 p-2 font-hand text-sm text-tb-navy" />
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={saveNotes}
                    data-testid="atlas-client-notes-save"
                    className="h-8 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs">Save</button>
                  <button type="button" onClick={() => { setEditingNotes(false); setNotesDraft(client.notes || ""); }}
                    className="h-8 px-3 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-xs">Cancel</button>
                </div>
              </div>
            ) : (
              <p className="font-hand text-sm text-tb-navy/80 whitespace-pre-wrap">
                {client.notes || <span className="text-tb-navy/40">No notes yet.</span>}
              </p>
            )}
          </div>
        </div>

        {/* Saved options */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-tb-navy font-semibold text-xl"
                style={{ fontFamily: '"Playfair Display", serif' }}>
              Saved options ({saves.length})
            </h2>
            <Link to="/atlas/search"
                  data-testid="atlas-client-goto-search"
                  className="font-hand text-xs text-tb-navy/60 hover:text-tb-navy">
              + Atlas Search →
            </Link>
          </div>
          {saves.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-6"
                 data-testid="atlas-client-empty-saves">
              <p className="font-hand text-tb-navy text-sm">No saved options yet.</p>
              <p className="mt-1 font-hand text-tb-navy/60 text-xs">
                Head to Atlas Search and click the bookmark on any result to build this client's shortlist.
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {saves.map((s) => (
                <SaveRow key={s.id} save={s} onDelete={() => deleteSave(s.id)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {editingProfile && (
        <ClientEditModal
          client={client}
          onClose={() => setEditingProfile(false)}
          onSaved={(c) => { setClient(c); setEditingProfile(false); }}
        />
      )}
    </div>
  );
}


function SaveRow({ save, onDelete }) {
  const snap = save.card_snapshot || {};
  const ov = save.live_overlays || {};
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState(save.advisor_notes || "");

  const saveNotes = async () => {
    try {
      const { data } = await api.patch(`/atlas/saves/${save.id}`, { advisor_notes: notesDraft });
      Object.assign(save, data.save);
      setNotesOpen(false);
      toast.success("Note updated.");
    } catch (e) { toast.error("Failed to update note."); }
  };

  return (
    <div className="rounded-xl border border-tb-navy/12 bg-white overflow-hidden"
         data-testid={`atlas-save-row-${save.id}`}>
      <div className="flex">
        {snap.image_url && (
          <div className="w-32 aspect-[3/2] bg-tb-navy/5 flex-shrink-0 overflow-hidden">
            <img src={snap.image_url} alt="" className="w-full h-full object-cover"
                 onError={(e) => { e.target.style.display = "none"; }} />
          </div>
        )}
        <div className="flex-1 p-3 min-w-0">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-tb-navy font-semibold text-sm leading-tight"
                 style={{ fontFamily: '"Playfair Display", serif' }}>
                {snap.title}
              </p>
              {snap.entity_name && snap.entity_name !== snap.title && (
                <p className="mt-0.5 font-hand text-[11px] text-tb-navy/60">{snap.entity_name}</p>
              )}
              {snap.snippet && (
                <p className="mt-1 font-hand text-tb-navy/70 text-xs line-clamp-2">{snap.snippet}</p>
              )}
              <div className="mt-1.5 flex items-center gap-2 font-hand text-[10px] text-tb-navy/50">
                <span>💾 Saved {new Date(save.saved_at).toLocaleDateString()}</span>
                {snap.publication_name && <span>· 📰 {snap.publication_name}</span>}
                {snap.url && (
                  <a href={snap.url} target="_blank" rel="noreferrer"
                     className="hover:text-tb-navy inline-flex items-center gap-0.5">
                    <ExternalLink className="w-2.5 h-2.5" /> source
                  </a>
                )}
              </div>
            </div>
            <button type="button" onClick={onDelete}
              data-testid={`atlas-save-delete-${save.id}`}
              className="w-7 h-7 rounded-full inline-flex items-center justify-center border border-red-200 text-red-700 hover:bg-red-50"
              title="Remove save">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>

          {/* Live overlays (re-hydrated on every load). */}
          {((ov.attribute_facts || []).length > 0 ||
             (ov.advisor_tips || []).length > 0 ||
             (ov.active_promos || []).length > 0) && (
            <div className="mt-2 pt-2 border-t border-tb-navy/8 flex flex-wrap items-center gap-1"
                 data-testid={`atlas-save-overlays-${save.id}`}>
              {(ov.attribute_facts || []).slice(0, 3).map((f) => (
                <span key={f.id} title={f.text}
                  className="text-[10px] font-hand rounded-full border border-tb-navy/15 bg-white px-2 py-0.5 text-tb-navy/80">
                  🔧 {f.text.length > 30 ? f.text.slice(0, 28) + "…" : f.text}
                </span>
              ))}
              {(ov.advisor_tips || []).length > 0 && (
                <span className="text-[10px] font-hand rounded-full bg-tb-gold-bright/20 border border-tb-gold-bright/70 text-tb-navy px-2 py-0.5">
                  💡 {ov.advisor_tips.length} tip{ov.advisor_tips.length === 1 ? "" : "s"}
                </span>
              )}
              {(ov.active_promos || []).length > 0 && (
                <span className="text-[10px] font-hand rounded-full bg-red-50 border border-red-300 text-red-800 px-2 py-0.5">
                  🎁 {ov.active_promos.length} active promo{ov.active_promos.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
          )}

          {/* Advisor notes */}
          <div className="mt-2 pt-2 border-t border-tb-navy/8">
            {notesOpen ? (
              <div>
                <textarea value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
                  data-testid={`atlas-save-notes-textarea-${save.id}`}
                  rows={2}
                  className="w-full rounded border border-tb-navy/20 p-1.5 font-hand text-xs text-tb-navy" />
                <div className="mt-1.5 flex gap-2">
                  <button type="button" onClick={saveNotes}
                    data-testid={`atlas-save-notes-save-${save.id}`}
                    className="h-7 px-2.5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-[11px]">Save</button>
                  <button type="button" onClick={() => { setNotesOpen(false); setNotesDraft(save.advisor_notes || ""); }}
                    className="h-7 px-2.5 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-[11px]">Cancel</button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setNotesOpen(true)}
                data-testid={`atlas-save-notes-edit-${save.id}`}
                className="text-left w-full font-hand text-[11px] text-tb-navy/70 hover:text-tb-navy">
                📝 {save.advisor_notes || <span className="text-tb-navy/40">Add advisor note…</span>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Create client modal (quick — just name)
// ─────────────────────────────────────────────────────────────────
function ClientCreateModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/atlas/clients", {
        display_name: name.trim(),
        email: email.trim() || null,
      });
      toast.success(`Created ${data.client.display_name}.`);
      onCreated(data.client);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create client.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="atlas-client-create-modal">
      <div className="absolute inset-0 bg-tb-navy/40" onClick={onClose} />
      <form onSubmit={submit}
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-tb-navy font-semibold text-xl"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            New client
          </h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-tb-navy/15 text-tb-navy/60">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Name *</label>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
          data-testid="atlas-client-create-name"
          placeholder="Sarah & Ben Johnson"
          className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
        <label className="block mt-3 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Email (optional)</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="atlas-client-create-email"
          placeholder="sarah@example.com"
          className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-10 px-4 rounded-full border border-tb-navy/20 font-hand text-sm text-tb-navy">
            Cancel
          </button>
          <button type="submit" disabled={busy || !name.trim()}
            data-testid="atlas-client-create-submit"
            className="h-10 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm inline-flex items-center gap-1.5 disabled:opacity-40">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Create
          </button>
        </div>
      </form>
    </div>
  );
}


function ClientEditModal({ client, onClose, onSaved }) {
  const [name, setName] = useState(client.display_name || "");
  const [email, setEmail] = useState(client.email || "");
  const [phone, setPhone] = useState(client.phone || "");
  const tp = client.travel_prefs || {};
  const [familySize, setFamilySize] = useState(tp.family_size || "");
  const [budget, setBudget] = useState(tp.budget_band || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await api.patch(`/atlas/clients/${client.id}`, {
        display_name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        travel_prefs: {
          family_size: familySize ? Number(familySize) : null,
          budget_band: budget || null,
          traveler_type: tp.traveler_type || null,
          kids_ages: tp.kids_ages || null,
          preferred_regions: tp.preferred_regions || null,
        },
      });
      toast.success("Updated.");
      onSaved(data.client);
    } catch (e) {
      toast.error("Failed to update.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-tb-navy/40" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-tb-navy font-semibold text-xl" style={{ fontFamily: '"Playfair Display", serif' }}>Edit client</h2>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-tb-navy/15 text-tb-navy/60"><X className="w-3.5 h-3.5" /></button>
        </div>
        <div className="grid gap-3">
          <div>
            <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
            </div>
            <div>
              <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Phone</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Family size</label>
              <input type="number" min="1" value={familySize} onChange={(e) => setFamilySize(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
            </div>
            <div>
              <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Budget</label>
              <select value={budget} onChange={(e) => setBudget(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy bg-white">
                <option value="">—</option>
                <option value="budget">Budget</option>
                <option value="mid">Mid</option>
                <option value="luxury">Luxury</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-10 px-4 rounded-full border border-tb-navy/20 font-hand text-sm text-tb-navy">Cancel</button>
          <button type="submit" disabled={busy}
            className="h-10 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm inline-flex items-center gap-1.5 disabled:opacity-40">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </form>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Client picker modal — used by AtlasSearchPage save flow
// ─────────────────────────────────────────────────────────────────
export function ClientPickerModal({ cardSnapshot, onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busySlug, setBusySlug] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/atlas/clients?q=${encodeURIComponent(q)}`);
        setClients(data.clients || []);
      } catch { setClients([]); } finally { setLoading(false); }
    })();
  }, [q]);

  const doSave = async (clientId, clientName) => {
    setBusySlug(clientId);
    try {
      const { data } = await api.post(`/atlas/clients/${clientId}/saves`, {
        card_snapshot: cardSnapshot,
      });
      if (data.already_saved) {
        toast.info(`Already in ${clientName}'s portfolio — updated timestamp.`);
      } else {
        toast.success(`Saved to ${clientName}'s portfolio.`);
      }
      onSaved?.(clientId, data.save.id, clientName);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save.");
    } finally { setBusySlug(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="atlas-client-picker-modal">
      <div className="absolute inset-0 bg-tb-navy/40" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-tb-navy font-semibold text-xl"
              style={{ fontFamily: '"Playfair Display", serif' }}>Save to client</h2>
          <button type="button" onClick={onClose}
            data-testid="atlas-client-picker-close"
            className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-tb-navy/15 text-tb-navy/60"><X className="w-3.5 h-3.5" /></button>
        </div>
        <p className="font-hand text-[11px] text-tb-navy/50 truncate mb-3">
          Saving: <span className="">{cardSnapshot.title}</span>
        </p>

        <div className="flex items-center gap-2 h-10 px-3 rounded-lg border border-tb-navy/20 mb-2">
          <SearchIcon className="w-3.5 h-3.5 text-tb-navy/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search clients…"
            data-testid="atlas-client-picker-search"
            className="flex-1 h-full outline-none font-hand text-sm text-tb-navy placeholder-tb-navy/40" />
        </div>

        <button type="button" onClick={() => setCreating(true)}
          data-testid="atlas-client-picker-new"
          className="w-full text-left rounded-lg border border-dashed border-tb-navy/25 bg-tb-navy/[0.02] p-2.5 font-hand text-sm text-tb-navy hover:bg-tb-navy/[0.05] mb-2 inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New client
        </button>

        <div className="max-h-72 overflow-y-auto space-y-1">
          {loading ? (
            <div className="font-hand text-[11px] text-tb-navy/50 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> loading…</div>
          ) : clients.length === 0 ? (
            <div className="font-hand text-xs text-tb-navy/50 py-3 text-center">
              {q ? "No matches." : "No clients yet — create one above."}
            </div>
          ) : (
            clients.map((c) => (
              <button key={c.id} type="button"
                onClick={() => doSave(c.id, c.display_name)}
                disabled={busySlug === c.id}
                data-testid={`atlas-client-picker-pick-${c.id}`}
                className="w-full text-left rounded-lg border border-tb-navy/10 bg-white p-2.5 hover:bg-tb-navy/5 disabled:opacity-40 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-hand text-sm text-tb-navy truncate">{c.display_name}</div>
                  <div className="font-hand text-[10px] text-tb-navy/50">{c.save_count || 0} saved</div>
                </div>
                {busySlug === c.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-tb-navy/50" />}
              </button>
            ))
          )}
        </div>
      </div>

      {creating && (
        <ClientCreateModal
          onClose={() => setCreating(false)}
          onCreated={(c) => { setCreating(false); doSave(c.id, c.display_name); }}
        />
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────
// Link existing TravelBio user → this advisor's roster.  Required
// before the Book-For-Client flow can award points to the client.
// TODO(invite-flow): fall back to sending an email invite if the
// email doesn't match an existing user.
// ─────────────────────────────────────────────────────────────────
function LinkExistingUserModal({ onClose, onLinked }) {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = (email.trim().length > 0) || (username.trim().length > 0);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/atlas/clients/link-existing", {
        email:        email.trim() || null,
        username:     username.trim() || null,
        display_name: displayName.trim() || null,
      });
      const c = data.client;
      if (data.already_linked) {
        toast.info(`Already linked: ${c.display_name}`);
      } else {
        toast.success(`Linked ${c.display_name}.`);
      }
      onLinked(c);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to link user.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         data-testid="atlas-client-link-modal">
      <div className="absolute inset-0 bg-tb-navy/40" onClick={onClose} />
      <form onSubmit={submit}
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-tb-navy font-semibold text-xl"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            Link existing user
          </h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-tb-navy/15 text-tb-navy/60">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <p className="font-hand text-xs text-tb-navy/60 mb-4">
          Look up an existing TravelBio account by email or username.
          Linking is required before you can book on their behalf so
          rewards land on their ledger.
        </p>

        <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Email</label>
        <input autoFocus type="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="atlas-client-link-email"
          placeholder="client@example.com"
          className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />

        <div className="mt-3 mb-1 flex items-center gap-2">
          <div className="flex-1 h-px bg-tb-navy/10" />
          <span className="font-hand text-[10px] uppercase text-tb-navy/40 tracking-widest">or</span>
          <div className="flex-1 h-px bg-tb-navy/10" />
        </div>

        <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Username</label>
        <input value={username}
          onChange={(e) => setUsername(e.target.value)}
          data-testid="atlas-client-link-username"
          placeholder="janedoe"
          className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />

        <label className="block mt-3 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Display name (optional)</label>
        <input value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          data-testid="atlas-client-link-display-name"
          placeholder="Overrides the user's name in your roster"
          className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-10 px-4 rounded-full border border-tb-navy/20 font-hand text-sm text-tb-navy">
            Cancel
          </button>
          <button type="submit" disabled={busy || !canSubmit}
            data-testid="atlas-client-link-submit"
            className="h-10 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm inline-flex items-center gap-1.5 disabled:opacity-40">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Link user
          </button>
        </div>
      </form>
    </div>
  );
}
